/** One person in GET sales-team/hierarchy/. `reports` nests to any depth. */
export interface SalesTeamNode {
  id: string;
  employee_number: number;
  name: string;
  email: string;
  is_active: boolean;
  designation: string;
  reports: SalesTeamNode[];
}

/** Derived from the designation string; the API carries no rank field. */
export enum SalesTeamLevel {
  RSM = 'rsm',
  ASM = 'asm',
  SO = 'so',
}

export interface SalesTeamSummary {
  totalCount: number;
  rsmCount: number;
  asmCount: number;
  soCount: number;
  /** ASMs and SOs at the top level, i.e. nobody has assigned them a manager yet. */
  unassignedCount: number;
  unassignedAsmCount: number;
  unassignedSoCount: number;
}

/** A node flattened for rendering, with what the connector lines need to know about its ancestors. */
export interface SalesTeamRow {
  node: SalesTeamNode;
  depth: number;
  isLastSibling: boolean;
  /** One entry per ancestor level: true where that ancestor has later siblings, so its vertical line continues. */
  guides: boolean[];
  isExpanded: boolean;
  /** A top-level ASM or SO: nobody has assigned them a manager yet. RSMs report to Admin by rule. */
  hasNoManager: boolean;
  /** Focus mode: the selected person. */
  isFocused: boolean;
  /** Focus mode: outside the selected person's branch, so shown greyed out. */
  isDimmed: boolean;
  /** Focus mode: which of this row's connector segments trace the focused path; null when nobody is focused. */
  path: SalesTeamRowPath | null;
}

export interface SalesTeamRowPath {
  /** Per guide column: the vertical line passing this row leads on to the focused path. */
  guides: boolean[];
  /** Upper half of this row's elbow: the parent's line reaches down to here on the path. */
  up: boolean;
  /** Lower half of the elbow: the parent's line carries on past this row to a later sibling on the path. */
  down: boolean;
  /** The arm from the parent's line into this row: this person is on the path. */
  across: boolean;
  /** The line from this person down to their reports: one of them is on the path. */
  stem: boolean;
  /**
   * Colour keys ('rsm', 'asm', 'so', 'other') for the segments above: each takes the level of the person it
   * leads into, matching the avatar colours. `line` is the parent's vertical line (up and down halves).
   */
  tones: { guides: string[]; line: string; across: string; stem: string };
}

/** The branch kept in colour while someone is focused. */
export interface SalesTeamFocus {
  /** The focused person, their managers up to Admin, and everyone below them. */
  ids: Set<string>;
  /** Their managers, top-level first. */
  ancestorIds: string[];
}

/** A reporting line as returned by PUT reporting-lines/{id}/ and, as an array, by bulk-assign. */
export interface ReportingLine {
  employee: string;
  employee_name: string;
  manager: string;
  manager_name: string;
  manager_designation: string;
  updated_at: string;
}

/** A person's place in the tree: who they report to, or null for Admin. */
export interface SalesTeamIndexEntry {
  node: SalesTeamNode;
  managerId: string | null;
}

/** A 400 from the reporting-lines endpoints, sorted by where the picker shows each message. */
export interface ReportingLineErrors {
  /** Under the manager picker. */
  manager: string[];
  /** About the selection as a whole, shown above it. */
  list: string[];
  /** Per employee id, shown on that employee in the selection. */
  byEmployee: Record<string, string[]>;
  /** Anything else the backend sent. */
  other: string[];
  /** The tree is out of date (a manager was deactivated or removed since it loaded). */
  refreshTree: boolean;
}
